"use client"
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { User, Mail, Lock, ArrowRight, AlertCircle, Code, Database, Server, Zap, Sparkles, Layers } from 'lucide-react';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const router = useRouter();
  const { colors } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await register(email, name, password);
      if (result === true) {
        router.push('/dashboard');
      } else {
        setError(result === false ? 'Registration failed. Please try again.' : result);
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    if (password.length === 0) return { strength: 0, text: '' };
    if (password.length < 6) return { strength: 1, text: 'Weak', color: colors.accent };
    if (password.length < 8) return { strength: 2, text: 'Fair', color: colors.secondary };
    return { strength: 3, text: 'Strong', color: '#10b981' };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-background relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(124,58,237,0.1) 1px, transparent 1px), linear-gradient(90deg,rgba(124,58,237,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
        
        {/* Radial Gradients */}
        <div 
          className="absolute top-0 right-1/4 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ background: `radial-gradient(circle, ${colors.primary}40, transparent 70%)` }}
        />
        <div 
          className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ background: `radial-gradient(circle, ${colors.secondary}40, transparent 70%)` }}
        />
        
        {/* Floating Icons */}
        <div 
          className="absolute top-16 right-12 animate-float"
          style={{ 
            animation: 'float 6s ease-in-out infinite',
            color: `${colors.primary}20`
          }}
        >
          <Layers className="w-16 h-16" />
        </div>
        <div 
          className="absolute top-32 left-20 animate-float-delayed"
          style={{ 
            animation: 'float 8s ease-in-out infinite 2s',
            color: `${colors.secondary}20`
          }}
        >
          <Code className="w-20 h-20" />
        </div>
        <div 
          className="absolute bottom-40 right-1/4 animate-float-slow"
          style={{ 
            animation: 'float 10s ease-in-out infinite 1s',
            color: `${colors.primary}15`
          }}
        >
          <Database className="w-14 h-14" />
        </div>
        <div 
          className="absolute bottom-24 left-16 animate-float"
          style={{ 
            animation: 'float 7s ease-in-out infinite 3s',
            color: `${colors.secondary}20`
          }}
        >
          <Zap className="w-18 h-18" />
        </div>
        <div 
          className="absolute top-1/2 left-10 animate-float-slow"
          style={{ 
            animation: 'float 9s ease-in-out infinite 4s',
            color: `${colors.accent}15`
          }}
        >
          <Sparkles className="w-12 h-12" />
        </div>
        <div 
          className="absolute top-1/3 right-1/3 animate-float"
          style={{ 
            animation: 'float 8s ease-in-out infinite 1.5s',
            color: `${colors.primary}15`
          }}
        >
          <Server className="w-16 h-16" />
        </div>
      </div>

      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/3 right-0 w-72 h-72 rounded-full blur-2xl opacity-10"
          style={{ 
            background: `radial-gradient(circle, ${colors.primary}, transparent)`,
            animation: 'pulse 4s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute bottom-1/3 left-0 w-96 h-96 rounded-full blur-2xl opacity-10"
          style={{ 
            background: `radial-gradient(circle, ${colors.secondary}, transparent)`,
            animation: 'pulse 5s ease-in-out infinite 2s'
          }}
        />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div
          className="rounded-3xl border border-border backdrop-blur-xl transition-all duration-300 hover:shadow-2xl bg-surface"
          style={{
            boxShadow: colors.shadow,
          }}
        >
          <div className="px-8 py-10">
            {/* Header */}
            <div className="text-center mb-8">
              {/* Logo/Brand Element */}
              <div className="flex items-center justify-center mb-6">
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-2"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    boxShadow: colors.shadow,
                  }}
                >
                  <Layers className="w-8 h-8 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold mb-2 text-primary-text">
                Create an account
              </h1>
              <p className="text-sm text-secondary-text">
                Get started with your free account today
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label 
                  className="block text-sm font-semibold mb-2 text-primary-text" 
                  htmlFor="name"
                >
                  Full name
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-text" />
                  <input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-border bg-surface-secondary text-primary-text placeholder-secondary-text transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
                    style={{
                      paddingLeft: '3rem',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}33`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '';
                      e.target.style.boxShadow = '';
                    }}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label 
                  className="block text-sm font-semibold mb-2 text-primary-text" 
                  htmlFor="email"
                >
                  Email address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-text" />
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-border bg-surface-secondary text-primary-text placeholder-secondary-text transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
                    style={{
                      paddingLeft: '3rem',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}33`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '';
                      e.target.style.boxShadow = '';
                    }}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label 
                  className="block text-sm font-semibold mb-2 text-primary-text" 
                  htmlFor="password"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-text" />
                  <input
                    id="password"
                    type="password"
                    placeholder="Create a strong password"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-border bg-surface-secondary text-primary-text placeholder-secondary-text transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
                    style={{
                      paddingLeft: '3rem',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.primary;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.primary}33`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '';
                      e.target.style.boxShadow = '';
                    }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                {/* Password Strength Indicator */}
                {password.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: `${colors.border}40` }}>
                        <div 
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            width: `${(passwordStrength.strength / 3) * 100}%`,
                            background: passwordStrength.color,
                          }}
                        />
                      </div>
                      <span 
                        className="text-xs font-medium"
                        style={{ color: passwordStrength.color }}
                      >
                        {passwordStrength.text}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div 
                  className="flex items-center gap-2 p-3 rounded-lg"
                  style={{
                    background: `${colors.accent}20`,
                    border: `1px solid ${colors.accent}40`,
                  }}
                >
                  <AlertCircle 
                    className="w-5 h-5 flex-shrink-0"
                    style={{ color: colors.accent }}
                  />
                  <p 
                    className="text-sm font-medium"
                    style={{ color: colors.accent }}
                  >
                    {error}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: '#ffffff',
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                  boxShadow: colors.shadow,
                  filter: colors.dropShadow,
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 12px 24px -4px ${colors.primary}40`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = colors.shadow;
                }}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Creating account...</span>
                  </>
                ) : (
                  <>
                    <span>Create account</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* Footer Links */}
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-center text-sm text-secondary-text">
                Already have an account?{' '}
                <Link 
                  href="/login" 
                  className="font-semibold text-primary hover:opacity-80 transition-all duration-200"
                >
                  Sign in instead
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RegisterPage;
